import { InputAdornment, Stack, TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { TextView } from "@components";
import ClearIcon from "@mui/icons-material/Clear";
import { ASSETS } from "../../utils/config";
import TabView from "./children/Tabs";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import SegmentsView from "./children/SegmentsView";
import _ from "lodash";
import { wheelPantryStyles as styles } from "./styles";
import Wheels from "./children/Wheels";
import { getLoggedInUser } from "../../utils/helpers";
import { getUserWheels } from "../../services/api";

const tabs = [
  {
    name: "Public",
    icon: ASSETS.publicDark,
    filter: "&is_public=true",
  },
  {
    name: "Private",
    icon: ASSETS.privateDark,
    filter: "&is_public=false",
  },
  {
    name: "Cloned",
    icon: ASSETS.cloneDark,
    filter: "&is_cloned=true",
  },
];

function WheelPantry({ onClose, onAddSegment, onLinkWheelPress, editorRef }) {
  const [selectedWheel, setSelectedWheel] = useState({});
  const [selectedSegment, setSelectedSegment] = useState({});
  const [searchedText, setSearchedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [wheelsData, setWheelsData] = useState({
    Public: { wheels: [], page: 0, total_pages: 0 },
    Private: { wheels: [], page: 0, total_pages: 0 },
    Cloned: { wheels: [], page: 0, total_pages: 0 },
  });
  const [selectedTab, setSelectedTab] = useState(tabs[0].name);
  const userId = getLoggedInUser()?.id;

  const fetchWheels = useCallback(
    async (tabName, currentPage, currentSearchedText) => {
      setIsLoading(true);
      const tab = tabs.find((tab) => tab.name === tabName);

      const query = currentSearchedText
        ? `${tab.filter}&content=${currentSearchedText}&page=${currentPage}`
        : `${tab.filter}&page=${currentPage}`;

      const response = await getUserWheels(userId, query);
      setWheelsData((prevData) => ({
        ...prevData,
        [tabName]: {
          wheels:
            currentPage === 1
              ? response.data.results
              : [...prevData[tabName].wheels, ...response.data.results],
          page: currentPage,
          total_pages: response.data.total_pages,
        },
      }));
      setIsLoading(false);
    },
    [userId]
  );

  useEffect(() => {
    fetchWheels(selectedTab, 1, searchedText);
  }, [selectedTab, fetchWheels]);

  // Debounced version of fetchWheels
  const debouncedFetchWheels = useCallback(
    _.debounce((tabName, currentPage, currentSearchedText) => {
      fetchWheels(tabName, currentPage, currentSearchedText);
    }, 300),
    [fetchWheels]
  );

  const handleTabChange = (newTab) => {
    setSelectedTab(newTab);
    setSearchedText("");
  };

  const handleLoadMore = () => {
    const nextPage = wheelsData[selectedTab].page + 1;
    if (nextPage <= wheelsData[selectedTab].total_pages) {
      fetchWheels(selectedTab, nextPage, searchedText);
    }
  };

  const handleSearch = (event) => {
    const newValue = event.target.value;
    setSearchedText(newValue);
    debouncedFetchWheels(selectedTab, 1, newValue);
  };

  return (
    <Stack style={styles.mainStack}>
      {_.isEmpty(selectedWheel) ? (
        <>
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              <img
                alt="icon"
                src={ASSETS.WheelPantry}
                style={styles.titleIcon}
              />
              <TextView style={styles.titleText}>Wheel Pantry</TextView>
            </Stack>
            <ClearIcon onClick={onClose} style={styles.clearIcon} />
          </Stack>

          <TextField
            sx={styles.searchTextField}
            label=""
            value={searchedText}
            variant="outlined"
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              style: styles.searchInput,
            }}
          />

          <TabView
            selectedTab={selectedTab}
            tabs={tabs}
            onTabChange={handleTabChange}
          />
          <Wheels
            wheels={wheelsData[selectedTab].wheels}
            onLoadMore={handleLoadMore}
            hideLoadMore={
              wheelsData[selectedTab].page + 1 <=
              wheelsData[selectedTab].total_pages
            }
            isLoading={isLoading}
            onWheelClick={setSelectedWheel}
          />
        </>
      ) : (
        <SegmentsView
          editorRef={editorRef}
          onWheelClick={setSelectedWheel}
          selectedWheel={selectedWheel}
          selectedSegment={selectedSegment}
          onSegmentClick={setSelectedSegment}
          onLinkWheelPress={onLinkWheelPress}
          onAddSegment={onAddSegment}
          onClickBack={() => {
            setSelectedWheel({});
            setSelectedSegment({});
          }}
        />
      )}
    </Stack>
  );
}

export default WheelPantry;
